'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { Button } from '@extern/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@extern/ui/input-otp';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { string, z } from 'zod';
import { AuthCard } from '@/app/(auth)/_partials/auth-card';
import { useSignUp } from '../_hooks/use-sign-up';
const FormSchema = z.object({
  code: string().min(6, {
    message: 'Please enter a valid code'
  })
});
export function SignUpEmailVerificationControl() {
  const $ = _c(34);
  const {
    onSignUpSubmitCode,
    onResendCode,
    isPending,
    isRedirecting,
    isResendDisabled,
    timeLeft,
    shouldRedirectToStart,
    goToStep
  } = useSignUp();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      resolver: zodResolver(FormSchema),
      defaultValues: {
        code: ""
      },
      mode: "onChange"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const form = useForm(t0);
  let t1;
  let t2;
  if ($[1] !== goToStep || $[2] !== shouldRedirectToStart) {
    t1 = () => {
      if (shouldRedirectToStart) {
        goToStep("start");
      }
    };
    t2 = [shouldRedirectToStart, goToStep];
    $[1] = goToStep;
    $[2] = shouldRedirectToStart;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== onSignUpSubmitCode) {
    t3 = function handleSubmit(data) {
      onSignUpSubmitCode(data.code);
    };
    $[5] = onSignUpSubmitCode;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleSubmit = t3;
  let t4;
  if ($[7] !== form || $[8] !== handleSubmit) {
    t4 = form.handleSubmit(handleSubmit);
    $[7] = form;
    $[8] = handleSubmit;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <AuthCard.Header><AuthCard.Title>Start Your Career Journey with Extern</AuthCard.Title><AuthCard.Description>Gain access to real-world projects, hands-on experience, and a community of professionals.</AuthCard.Description></AuthCard.Header>;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== form.control) {
    t6 = <AuthCard.Content><FormField control={form.control} name="code" render={_temp} /></AuthCard.Content>;
    $[11] = form.control;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = isPending || isRedirecting || !form.formState.isValid;
  const t8 = isPending || isRedirecting;
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <Button className="w-full" disabled={t7} isLoading={t8} type="submit">Submit</Button>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = isResendDisabled ? `${timeLeft}s` : "60s";
  let t11;
  if ($[16] !== t10) {
    t11 = <span className="text-role-info">Resend ({t10})</span>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== isResendDisabled || $[19] !== onResendCode || $[20] !== t11) {
    t12 = <Button appearance="ghost" className="gap-0" disabled={isResendDisabled} onClick={onResendCode} type="button">Didn't receive a code? {t11}</Button>;
    $[18] = isResendDisabled;
    $[19] = onResendCode;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t9) {
    t13 = <AuthCard.Footer>{t9}{t12}</AuthCard.Footer>;
    $[22] = t12;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13 || $[26] !== t6) {
    t14 = <AuthCard>{t5}{t6}{t13}</AuthCard>;
    $[25] = t13;
    $[26] = t6;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== t4) {
    t15 = <form onSubmit={t4}>{t14}</form>;
    $[28] = t14;
    $[29] = t4;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== form || $[32] !== t15) {
    t16 = <Form {...form}>{t15}</Form>;
    $[31] = form;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  return t16;
}
function _temp(t0) {
  const {
    field
  } = t0;
  return <FormItem><FormLabel>Email verification code</FormLabel><FormControl><InputOTP data-cy="sign-up-email-otp" maxLength={6} {...field}><InputOTPGroup className="h-full w-full"><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={0} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={1} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={2} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={3} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={4} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={5} /></InputOTPGroup></InputOTP></FormControl><FormDescription>Please enter the code sent to your email</FormDescription><FormMessage /></FormItem>;
}